import { Archive, Copy, LayoutDashboard, LayoutGrid, MapPin, Pencil, Share2, User } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { CartContainer, EntityList } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { DASHBOARD_STATES } from '../constants';
import { useDashboardDisplayTitle } from '../hooks';
import { timeAgo } from '../utils';
import {
  stopEntityListInteractiveKeyDown,
  stopEntityListInteractivePropagation,
  WIDGET_ENTITY_LIST_DROPDOWN_PROPS,
  wrapEntityListMenuAction,
} from '../../widgets/utils';

/**
 * State → DS badge variant and icon tint. One map, so the JSX never branches
 * per state. Tints are `IconHighlight` token names, never colour literals.
 */
const STATE_STYLE = {
  [DASHBOARD_STATES.PUBLISHED]: { variant: 'success', tint: 'Success', labelKey: 'status.published' },
  [DASHBOARD_STATES.ARCHIVED]: { variant: 'warning', tint: 'Alert', labelKey: 'status.archived' },
  [DASHBOARD_STATES.DRAFT]: { variant: 'neutral', tint: 'LightBlue', labelKey: 'status.draft' },
};

/**
 * Makes a long title truncate instead of wrapping the row — the same scoped
 * override the widget library row uses, since `EntityList`'s title is
 * `shrink-0` inside a `flex-wrap` header. The title is the header's 2nd child
 * because this card always passes `icon`.
 */
const DASHBOARD_CARD_CLASS = [
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:min-w-0',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:shrink',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:overflow-hidden',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:truncate',
].join(' ');

/**
 * A dashboard (or template) in a list — `CartContainer` + `EntityList`, the
 * design-system row governance and the widget library already use, instead of
 * a hand-built card with its own colours.
 *
 * Clicking the row opens the dashboard, or the template preview when
 * `onPreview` is given; templates have no actions menu.
 */
const DashboardBuilderCard = ({ dashboard, onShare, onArchive, onDuplicate, onPreview }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboards');
  const displayTitle = useDashboardDisplayTitle();

  const stateStyle = STATE_STYLE[dashboard.state] ?? STATE_STYLE[DASHBOARD_STATES.DRAFT];
  const isArchived = dashboard.state === DASHBOARD_STATES.ARCHIVED;
  const dashboardTitle = displayTitle(dashboard);
  const dashboardDescription = dashboard.descriptionKey
    ? t(dashboard.descriptionKey, { defaultValue: dashboard.description || '' })
    : dashboard.description;

  const handleOpenDashboard = useCallback(() => {
    if (onPreview) {
      onPreview(dashboard);
      return;
    }
    navigate(`/dashboards/${dashboard.id}`);
  }, [dashboard, navigate, onPreview]);
  const handleEdit = useCallback(() => navigate(`/dashboards/${dashboard.id}/edit`), [dashboard.id, navigate]);
  const handleShare = useCallback(() => onShare?.(dashboard.id), [dashboard.id, onShare]);
  const handleArchive = useCallback(() => onArchive?.(dashboard.id), [dashboard.id, onArchive]);
  const handleDuplicate = useCallback(() => onDuplicate?.(dashboard.id), [dashboard.id, onDuplicate]);

  const status = useMemo(
    () => ({ label: t(stateStyle.labelKey), variant: stateStyle.variant }),
    [stateStyle, t],
  );

  const primaryMeta = useMemo(
    () => (dashboard.sectionPath
      ? [{ icon: MapPin, label: dashboard.sectionPath, tooltip: dashboard.sectionPath }]
      : []),
    [dashboard.sectionPath],
  );

  const secondaryMeta = useMemo(
    // No creator is left out rather than printed as "-": a lone dash next to
    // an icon reads as a broken field.
    () => [
      ...(dashboard.creatorName ? [{ icon: User, label: dashboard.creatorName }] : []),
      { icon: LayoutGrid, label: t('card.widgets', { count: dashboard.widgetCount ?? 0 }) },
    ],
    [dashboard.creatorName, dashboard.widgetCount, t],
  );

  const tags = useMemo(() => (dashboard.tags ?? []).map((tag) => ({ label: tag })), [dashboard.tags]);

  const dropdown = useMemo(() => {
    if (onPreview) return undefined;
    return {
      props: {
        ...WIDGET_ENTITY_LIST_DROPDOWN_PROPS,
        triggerAriaLabel: t('card.actionsFor', { name: dashboardTitle }),
      },
      items: [
        { label: t('menu.edit'), icon: Pencil, onClick: wrapEntityListMenuAction(handleEdit) },
        /*
          Sharing is off until it is real (CT-5893): the directory behind the
          modal is a fixed list of invented people and `shareDashboard` only
          mutates an in-memory store. A disabled menu item cannot carry a
          tooltip — `disabled:pointer-events-none` swallows the hover — so the
          reason goes in the label.
        */
        { label: t('menu.shareUnavailable'), icon: Share2, disabled: true },
        { label: t('menu.duplicate'), icon: Copy, onClick: wrapEntityListMenuAction(handleDuplicate) },
        { separator: true },
        {
          label: isArchived ? t('menu.unarchive') : t('menu.archive'),
          icon: Archive,
          onClick: wrapEntityListMenuAction(handleArchive),
        },
      ],
    };
  }, [dashboardTitle, handleArchive, handleDuplicate, handleEdit, handleShare, isArchived, onPreview, t]);

  return (
    <CartContainer size="S" variant="neutral" onClick={handleOpenDashboard}>
      <EntityList
        // Keeps the kebab from also opening the dashboard, by mouse and by keyboard.
        onClick={stopEntityListInteractivePropagation}
        onKeyDown={stopEntityListInteractiveKeyDown}
        className={DASHBOARD_CARD_CLASS}
        icon={LayoutDashboard}
        iconHighlight={{ bgColor: stateStyle.tint, iconColor: stateStyle.tint }}
        title={dashboardTitle}
        primaryMeta={primaryMeta}
        status={status}
        timestamp={timeAgo(dashboard.updatedAt, i18n.language) ?? undefined}
        description={dashboardDescription || undefined}
        descriptionMaxLength={160}
        secondaryMeta={secondaryMeta}
        tags={tags}
        dropdown={dropdown}
      />
    </CartContainer>
  );
};

DashboardBuilderCard.propTypes = {
  dashboard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    state: PropTypes.string,
    description: PropTypes.string,
    descriptionKey: PropTypes.string,
    sectionPath: PropTypes.string,
    creatorName: PropTypes.string,
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    widgetCount: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onShare: PropTypes.func,
  onArchive: PropTypes.func,
  onDuplicate: PropTypes.func,
  onPreview: PropTypes.func,
};

export default memo(DashboardBuilderCard);
